import { LRUCache } from 'lru-cache';
import { useMemo } from 'react';
import { Observable } from 'rxjs';
import { v4 as uuid } from 'uuid';
import { useSecuritiesContext } from '../contexts';
import { useSocketClient } from '../providers/WebSocketClientProvider';
import { MARKET_DATA_SNAPSHOT } from '../tokens';
import {
  DepthTypeEnum,
  FeeModeEnum,
  LiquidityTypeEnum,
  type MarketDataSnapshot,
  type MarketDataSnapshotRequest,
  type SubscriptionResponse,
} from '../types';
import { EMPTY_ARRAY } from '../utils';
import { useSyncedRef } from './useSyncedRef';

/**
 * We can have multiple active MDS subscriptions for the same Symbol (for example, different Markets selections).
 * `requestKey` preserves which logical request produced the snapshot so downstream consumers can disambiguate/cache correctly.
 */
export interface KeyedMarketDataSnapshot extends MarketDataSnapshot {
  requestKey: string;
}

export interface UseMultiMarketDataSnapshotOutput {
  cache: Map<string, KeyedMarketDataSnapshot>;
  json: SubscriptionResponse<KeyedMarketDataSnapshot>;
}

export interface UseMultiMarketDataSnapshotRequest {
  key: string;
  Symbol: string;
  Markets?: string[];
}

export function getMultiMarketDataSnapshotRequestKey({
  Symbol,
  Markets,
}: Pick<UseMultiMarketDataSnapshotRequest, 'Symbol' | 'Markets'>) {
  return [Symbol, ...(Markets ? [...Markets].toSorted() : EMPTY_ARRAY)].join('|');
}

function getMarketName(marketOrMarketAccount: string) {
  return marketOrMarketAccount.split('/')[0];
}

/** Request markets can be a greater set than that defined by the security we are making the request for. We return the intersection so we only make valid MDS requests. */
function intersectMarkets({
  requestMarkets,
  securityMarkets,
}: {
  requestMarkets: string[] | undefined;
  securityMarkets: string[];
}) {
  if (requestMarkets == null) {
    return securityMarkets;
  }

  const securityMarketSet = new Set(securityMarkets);
  return requestMarkets.filter(marketOrMarketAccount => securityMarketSet.has(getMarketName(marketOrMarketAccount)));
}

interface UseMultiMarketDataParams {
  /** One request entry per MDS stream. This should be a ReplaySubject. */
  requestsObs: Observable<UseMultiMarketDataSnapshotRequest[]>;
  /** The maximum amount of subscriptions we dare keep open with the backend */
  maxActiveSubscriptions: number;
  /** The tag to put on the subscription for better debugging of all related streams to the hook invocation */
  subscriptionTag?: string;
  showFirmLiquidity?: boolean;
  showAllInPrices?: boolean;

  /** Pass in overrides to apply to _every_ MDS request */
  requestOverrides?: Partial<MarketDataSnapshotRequest>;
}

export const useMultiMarketDataSnapshot = ({
  requestsObs,
  maxActiveSubscriptions,
  subscriptionTag,
  showFirmLiquidity,
  showAllInPrices,

  requestOverrides,
}: UseMultiMarketDataParams) => {
  const websocketClient = useSocketClient();
  const { securitiesBySymbol } = useSecuritiesContext();

  /**
   * We just make this a synced ref so we dont need to handle with the case where securitiesBySymbol updates in the pipe. Its
   * fine if we fall slightly out of date. In the worst case, a refresh will always fix any problem the user has.
   */
  const securitiesBySymbolRef = useSyncedRef(securitiesBySymbol);

  const marketDataObs = useMemo(
    () =>
      new Observable<UseMultiMarketDataSnapshotOutput>(output => {
        // The cache is a key-value store representing our registered subscriptions with the websocket client.
        // Keys are request keys, and values are subscription "addresses" (their unique identifier)
        const activeSubscriptionsByKey = new LRUCache<string, string>({
          max: maxActiveSubscriptions,
          dispose: (address, key) => {
            marketDataByKey.delete(key);
            websocketClient.unregisterSubscription(address);
          },
          // Whether or not to increase the usage metric of this cache entry when we check if it exists in the cache (calling cache.has(...)).
          // This is disabled by default, but something we want in this use case.
          updateAgeOnHas: true,
        });

        const marketDataByKey = new Map<string, KeyedMarketDataSnapshot>();

        const requestsSub = requestsObs.subscribe(newRequests => {
          for (const requestItem of newRequests) {
            const { key, Symbol, Markets } = requestItem;
            const isRequestRegistered = activeSubscriptionsByKey.has(key);
            // if request is registered, or symbol/key is falsy, skip this request
            if (isRequestRegistered || !Symbol || !key) {
              continue;
            }

            const address = uuid();
            const securityMarkets = securitiesBySymbolRef.current.get(Symbol)?.Markets ?? EMPTY_ARRAY;
            const request: MarketDataSnapshotRequest = {
              name: MARKET_DATA_SNAPSHOT,
              DepthType: DepthTypeEnum.Price,
              Symbol,
              Depth: 3,
              tag: subscriptionTag ?? 'MultiMarketDataSnapshot',
              // For backend performance reasons, we send all markets on the security.
              // If request-level markets are provided, we constrain them to only those that belong to the security.
              // The fallback (undesireable) is we send an empty array, which can lead to performance issues on BE.
              Markets: intersectMarkets({ requestMarkets: Markets, securityMarkets }),
              LiquidityType: showFirmLiquidity ? LiquidityTypeEnum.Firm : LiquidityTypeEnum.Indicative,
              FeeMode: showAllInPrices ? FeeModeEnum.Taker : undefined,
              ...requestOverrides,
            };

            websocketClient.registerSubscription(address, [request], (err, json) => {
              if (err) {
                websocketClient.unregisterSubscription(address);
                activeSubscriptionsByKey.delete(key);
              }

              if (json && json.error) {
                //
              } else if (json && json.data && Array.isArray(json.data)) {
                const data = (json.data as MarketDataSnapshot[]).map(update => ({ ...update, requestKey: key }));
                for (const keyedSnapshot of data) {
                  marketDataByKey.set(key, keyedSnapshot);
                }
                // Were combining a bunch of streams and none of them are delta updates anyway so just set initial to always be false
                output.next({
                  cache: marketDataByKey,
                  json: { ...json, data, initial: false },
                } satisfies UseMultiMarketDataSnapshotOutput);
              }
            });
            activeSubscriptionsByKey.set(key, address);
          }
        });

        return () => {
          requestsSub.unsubscribe();
          activeSubscriptionsByKey.forEach(address => websocketClient.unregisterSubscription(address));
        };
      }),
    [
      requestsObs,
      websocketClient,
      maxActiveSubscriptions,
      subscriptionTag,
      securitiesBySymbolRef,
      showFirmLiquidity,
      showAllInPrices,
      requestOverrides,
    ]
  );

  return marketDataObs;
};
